/**
 * CollegePlanForm Component
 * WP-RETIRE-13: Guided college expense planning.
 *
 * Captures what a parent actually knows — a child's age, years of study and
 * annual cost — and generates ordinary retirement events from it. It creates
 * no new event type: each child becomes a `cash_flow` event with a negative
 * amount spanning the tuition years, editable afterwards in the events panel
 * like any other event.
 *
 * Modelling notes:
 * - The projection engine is inflation-adjusted, so costs are entered in
 *   TODAY'S dollars and general inflation is NOT applied (that would
 *   double-count it). Only above-inflation escalation is modelled, and it is
 *   a user-controlled assumption defaulting to 0.
 * - No default annual cost is supplied. A figure here would go straight into
 *   the user's financial projection, so it must come from them rather than be
 *   invented.
 *
 * Privacy: a child's name is third-party PII about a minor. The label defaults
 * to a generic "College - Child N" and only ever holds a name the user typed.
 */
import { useMemo, useState } from 'react';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { dollarsToCents, centsToDollars, formatCurrency } from '@/utils/format';
import {
  DEFAULT_COLLEGE_START_AGE,
  DEFAULT_REAL_COST_ESCALATION,
  DEFAULT_YEARS_OF_STUDY,
  buildCollegeEvents,
  totalCollegeCostCents,
  type CollegeChildInput,
} from '@/utils/collegePlanning';
import type { RetirementEventCreate } from '@/types/retirement';

interface ChildRow {
  key: string;
  label: string;
  currentAge: string;
  yearsOfStudy: string;
  annualCostDollars: string;
}

export interface CollegePlanFormProps {
  /** Called with one event per child. */
  onSubmit: (events: RetirementEventCreate[]) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Injected for testability; defaults to the real current year. */
  currentYear?: number;
}

function emptyRow(): ChildRow {
  return {
    key: Math.random().toString(36).slice(2),
    label: '',
    currentAge: '',
    yearsOfStudy: String(DEFAULT_YEARS_OF_STUDY),
    annualCostDollars: '',
  };
}

export default function CollegePlanForm({
  onSubmit,
  onCancel,
  isSubmitting = false,
  currentYear,
}: CollegePlanFormProps) {
  const [rows, setRows] = useState<ChildRow[]>([emptyRow()]);
  const [escalationPercent, setEscalationPercent] = useState(
    String(DEFAULT_REAL_COST_ESCALATION * 100)
  );

  const year = currentYear ?? new Date().getFullYear();

  const options = useMemo(
    () => ({
      currentYear: year,
      collegeStartAge: DEFAULT_COLLEGE_START_AGE,
      realCostEscalationRate: (Number(escalationPercent) || 0) / 100,
    }),
    [year, escalationPercent]
  );

  const children: CollegeChildInput[] = useMemo(
    () =>
      rows.map((row) => ({
        label: row.label,
        currentAge: row.currentAge === '' ? null : Number(row.currentAge),
        yearsOfStudy: Number(row.yearsOfStudy) || 0,
        annualCostCents:
          row.annualCostDollars === ''
            ? 0
            : dollarsToCents(Number(row.annualCostDollars)),
      })),
    [rows]
  );

  const events = useMemo(
    () => buildCollegeEvents(children, options),
    [children, options]
  );
  const totalCents = useMemo(
    () => totalCollegeCostCents(children, options),
    [children, options]
  );

  const updateRow = (key: string, patch: Partial<ChildRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  };

  const canSubmit = events.length > 0 && !isSubmitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(events);
      }}
      className="space-y-4"
      aria-label="College expense plan"
    >
      <p className="text-sm text-gray-600">
        Enter each child&apos;s details to add their college costs to your
        projection. Amounts are in today&apos;s dollars.
      </p>

      {rows.map((row, index) => (
        <fieldset
          key={row.key}
          className="rounded-md border border-gray-200 p-3 space-y-3"
        >
          <legend className="px-1 text-sm font-medium text-gray-700">
            Child {index + 1}
          </legend>

          <Input
            label="Label (optional)"
            placeholder={`College - Child ${index + 1}`}
            value={row.label}
            onChange={(e) => updateRow(row.key, { label: e.target.value })}
            maxLength={100}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label="Current age"
              type="number"
              min={0}
              max={30}
              value={row.currentAge}
              onChange={(e) => updateRow(row.key, { currentAge: e.target.value })}
            />
            <Input
              label="Years of study"
              type="number"
              min={1}
              max={10}
              value={row.yearsOfStudy}
              onChange={(e) =>
                updateRow(row.key, { yearsOfStudy: e.target.value })
              }
            />
            <Input
              label="Annual cost"
              type="number"
              min={0}
              step="1000"
              placeholder="e.g. 30000"
              value={row.annualCostDollars}
              onChange={(e) =>
                updateRow(row.key, { annualCostDollars: e.target.value })
              }
            />
          </div>
        </fieldset>
      ))}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRows((prev) => [...prev, emptyRow()])}
        >
          Add another child
        </Button>
        {rows.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setRows((prev) => prev.slice(0, -1))}
          >
            Remove last
          </Button>
        )}
      </div>

      <div className="rounded-md bg-gray-50 p-3 space-y-2">
        <Input
          label="Cost growth above inflation (% per year)"
          type="number"
          min={0}
          max={10}
          step="0.5"
          value={escalationPercent}
          onChange={(e) => setEscalationPercent(e.target.value)}
        />
        <p className="text-xs text-gray-500">
          Your projection is already inflation-adjusted, so enter only how much
          faster you expect college costs to rise than inflation generally.
          Leave at 0 to assume they rise in line with it.
        </p>
      </div>

      {events.length > 0 && (
        <div className="rounded-md bg-blue-50 p-3">
          <p className="text-sm text-gray-800">
            Adds {events.length} {events.length === 1 ? 'expense' : 'expenses'}{' '}
            totalling{' '}
            <span className="font-semibold">
              {formatCurrency(centsToDollars(totalCents), 'USD')}
            </span>{' '}
            in today&apos;s dollars.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit} isLoading={isSubmitting}>
          Add to plan
        </Button>
      </div>
    </form>
  );
}
